import React, { useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Stack, Typography, Grid } from '@mui/material';
import { useMatch, useSearchParams } from 'react-router';
import BuildingsLoader from '../loaders/BuildingsLoader';
import LocationLoader from '../loaders/LocationLoader';
import OrganizationsLoader from '../loaders/OrganizationsLoader';
import EquipTypeLoader from '../loaders/EquipTypeLoader';
import UnitsLoader from '../loaders/UnitsLoader';
import PointTypeLoader from '../loaders/PointTypeLoader';
import MeasurementsLoader from '../loaders/MeasurementsLoader';
import EquipmentLoader from '../loaders/EquipmentLoader';
import {
  StagingDevicesLoader,
  StagingPointsLoader,
  StagingEquipmentLoader,
  StagingPointsValidationLoader,
  StagingEquipmentValidationLoader,
} from '../loaders/staging';
import { DeploymentsLoader, DeploymentDiscoveryLoader } from '../loaders/deployments';
import { AUTH_LEVELS } from '../auth/Authorized';
import Devices from './views/Devices';
import Points from './views/Points';
import Equipment from './views/Equipment';
import Locations from './views/Locations';
import Overview from './views/Overview';
import { Fill } from '../util/Page';
import { capitalize } from '../../utils/strings';
import ChangelogFilters from '../util/ChangelogFilters';
import Tabs from './Tabs';
import {
  extractSortingParams,
  extractFilterParams,
  sortingUpdater,
  filterUpdater,
  jsonReplacer,
} from '../util/data-table';
import { defaultFilters } from './columns';

const VIEWS = {
  overview: Overview,
  devices: Devices,
  equipment: Equipment,
  points: Points,
  locations: Locations,
};

const Staging2 = ({ userOrgId, buildingsById, deployments, navigate }) => {
  const match = useMatch(`/staging/:bid/:view`);
  const { bid: buildingId, view } = match?.params ?? {};
  const building = buildingsById[buildingId];
  const [orgId, setOrgId] = useState(building?.orgId ?? userOrgId);
  const [searchParams, setSearchParams] = useSearchParams();
  const sorting = useMemo(() => extractSortingParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => extractFilterParams(searchParams), [searchParams]);
  const onSortingChange = useCallback(
    (updater) => setSearchParams(sortingUpdater(sorting)(updater)),
    [setSearchParams, sorting]
  );
  const onColumnFiltersChange = useCallback(
    (updater) => setSearchParams(filterUpdater(columnFilters)(updater)),
    [setSearchParams, columnFilters]
  );

  const buildingName = building?.name;
  const setUrlAndFilters = ({ buildings, orgId: org }) => {
    if (buildings.length === 1) {
      // When building changes, if no column filters defined, set default filters
      navigate(
        `/staging/${buildings[0]}/overview?filterBy=${JSON.stringify(defaultFilters, jsonReplacer)}`
      );
    } else {
      setOrgId(org);
    }
  };

  return (
    <Grid>
      <OrganizationsLoader />
      <BuildingsLoader />
      <MeasurementsLoader />
      <UnitsLoader />
      <PointTypeLoader />
      <EquipTypeLoader />
      <DeploymentsLoader />
      {building ? (
        <>
          <StagingDevicesLoader key={`sd-${building.id}`} buildingId={building.id} />
          <StagingEquipmentLoader key={`se-${building.id}`} buildingId={building.id} />
          <StagingPointsLoader key={`sp-${building.id}`} buildingId={building.id} />
          <StagingEquipmentValidationLoader key={`sve-${building.id}`} buildingId={building.id} />
          <StagingPointsValidationLoader key={`svp-${building.id}`} buildingId={building.id} />
          <EquipmentLoader key={`e-${building.id}`} buildingId={building.id} getPoints getDevices />
          <LocationLoader key={`ll-${building.id}`} buildingId={building.id} />
          {deployments
            .filter((d) => d.buildings.includes(building.id))
            .map((d) => (
              <DeploymentDiscoveryLoader key={`dc-${d.site}`} site={d.site} />
            ))}
        </>
      ) : null}

      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="h6">Staging{buildingName && ` - ${buildingName}`}</Typography>
        <Fill />
        <ChangelogFilters
          filters={{
            orgId,
            buildings:
              building && (orgId === null || orgId === building?.orgId) ? [buildingId] : [],
          }}
          setFilters={setUrlAndFilters}
          noDates
          singleBuilding
        />
      </Stack>
      {building && view ? (
        <>
          <Tabs searchParams={searchParams} buildingId={buildingId} view={view} />
          <title>{`${building.name} Staging ${capitalize(view)}`}</title>
          {React.createElement(VIEWS[view], {
            sorting,
            onSortingChange,
            columnFilters,
            onColumnFiltersChange,
            building,
          })}
        </>
      ) : (
        <>
          <br />
          <Typography align="center">Please select a building to proceed.</Typography>
        </>
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: {
      buildings: { byId },
      deployments: { deployments },
    },
    user: { orgId, userAuthLevel },
  } = state;

  return {
    userOrgId: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN ? null : orgId,
    buildingsById: byId,
    deployments: Object.values(deployments),
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Staging2);
